import { objectBounds } from './ObjectArtwork';

export const GRID_STEP = 20;

export function worldBounds(o) {
  const a = anchors(o);
  return { x: a.x[0], y: a.y[0], w: a.x[2] - a.x[0], h: a.y[2] - a.y[0], cx: o.x, cy: o.y };
}
export function groupBounds(objects) {
  const list = objects.map(worldBounds);
  if (!list.length) return null;
  const x = Math.min(...list.map(b => b.x)), y = Math.min(...list.map(b => b.y));
  return { x, y, w: Math.max(...list.map(b => b.x + b.w)) - x, h: Math.max(...list.map(b => b.y + b.h)) - y };
}
// Returns {id: {x,y}} moving every object so its edge/centre meets the group's (or area's) reference line.
export function alignGroup(objects, mode, area) {
  const group = objects.length > 1 ? groupBounds(objects) : { x: area.x, y: area.y, w: area.w, h: area.h };
  const patches = {};
  for (const o of objects) {
    const b = worldBounds(o), p = {};
    if (mode === 'left') p.x = o.x + (group.x - b.x);
    if (mode === 'right') p.x = o.x + (group.x + group.w - (b.x + b.w));
    if (mode === 'hcenter') p.x = o.x + (group.x + group.w / 2 - (b.x + b.w / 2));
    if (mode === 'top') p.y = o.y + (group.y - b.y);
    if (mode === 'bottom') p.y = o.y + (group.y + group.h - (b.y + b.h));
    if (mode === 'vcenter') p.y = o.y + (group.y + group.h / 2 - (b.y + b.h / 2));
    if (mode === 'area') { p.x = o.x + (area.cx - (b.x + b.w / 2)); p.y = o.y + (area.cy - (b.y + b.h / 2)); }
    patches[o.id] = p;
  }
  return patches;
}
export function distributeGroup(objects, axis) {
  const key = axis === 'x' ? 'x' : 'y', size = axis === 'x' ? 'w' : 'h';
  const sorted = [...objects].map(o => ({ o, b: worldBounds(o) })).sort((a, b) => a.b[key] - b.b[key]);
  if (sorted.length < 3) return {};
  const first = sorted[0], last = sorted[sorted.length - 1];
  const span = (last.b[key] + last.b[size]) - first.b[key], total = sorted.reduce((sum, item) => sum + item.b[size], 0);
  const gap = (span - total) / (sorted.length - 1);
  let cursor = first.b[key];
  const patches = {};
  for (const item of sorted) { patches[item.o.id] = { [key]: item.o[key] + (cursor - item.b[key]) }; cursor += item.b[size] + gap; }
  return patches;
}

function anchors(o) {
  const b = objectBounds(o), angle = (o.rotation || 0) * Math.PI / 180, scale = o.scale || 1;
  const points = [[b.x, b.y], [b.x + b.w, b.y], [b.x, b.y + b.h], [b.x + b.w, b.y + b.h]].map(([x, y]) => ({ x: o.x + (x * Math.cos(angle) - y * Math.sin(angle)) * scale, y: o.y + (x * Math.sin(angle) + y * Math.cos(angle)) * scale }));
  return { x: [Math.min(...points.map(p => p.x)), o.x, Math.max(...points.map(p => p.x))], y: [Math.min(...points.map(p => p.y)), o.y, Math.max(...points.map(p => p.y))] };
}

// `rulerGuides`: [{axis, value}] customer guide lines (canvas units); they snap like object edges.
export function alignObject(object, objects, unitsPerPixel, snapEnabled, gridEnabled, centre = { x: 300, y: 300 }, rulerGuides = []) {
  const active = anchors(object);
  const threshold = unitsPerPixel * (snapEnabled ? 7 : 2);
  const guides = [];
  const patch = { x: object.x, y: object.y };
  for (const axis of ['x', 'y']) {
    const targets = [];
    objects.filter(o => o.id !== object.id && o.visible).forEach(o => anchors(o)[axis].forEach(value => targets.push({ value, objectId: o.id })));
    rulerGuides.filter(g => g.axis === axis).forEach(g => targets.push({ value: g.value, guide: true }));
    // Explicit grid mode is authoritative: object anchors must never pull an axis off-grid.
    if (snapEnabled && gridEnabled) {
      patch[axis] = centre[axis] + Math.round((object[axis] - centre[axis]) / GRID_STEP) * GRID_STEP;
      if (Math.abs(patch[axis] - centre[axis]) < .01) guides.push({ axis, value: centre[axis], centre: true });
      else {
        const shifted = active[axis].map(value => value + patch[axis] - object[axis]);
        const match = targets.find(target => shifted.some(value => Math.abs(value - target.value) <= .5));
        if (match) guides.push({ axis, value: match.value, centre: false, objectId: match.objectId });
      }
      continue;
    }
    // In smart-guide mode, centre alignment takes precedence over nearby object edges.
    if (Math.abs(object[axis] - centre[axis]) <= threshold) {
      if (snapEnabled) patch[axis] = centre[axis];
      guides.push({ axis, value: centre[axis], centre: true });
      continue;
    }
    let nearest = null;
    active[axis].forEach(anchor => targets.forEach(target => {
      const delta = target.value - anchor;
      if (Math.abs(delta) <= threshold && (!nearest || Math.abs(delta) < Math.abs(nearest.delta))) nearest = { ...target, delta };
    }));
    if (nearest) {
      if (snapEnabled) patch[axis] += nearest.delta;
      guides.push({ axis, value: nearest.value, centre: !!nearest.centre, objectId: nearest.objectId, guide: !!nearest.guide });
    }
  }
  return { patch, guides };
}